/**
 * VendorPortalProductEventLimitService
 * Service for managing vendor product event limits.
 * Route: api/vendor-portal/product-event-limits
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalProductEventLimitService
 * Handles vendor product event limit operations for the vendor portal.
 * Vendors can manage quantity limits for their products at specific events.
 * Maps to /api/vendor-portal/product-event-limits endpoint
 */
export class VendorPortalProductEventLimitService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalProductEventLimitService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalProductEventLimitService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET BY EVENT ====================
    /**
     * Get all product event limits for a specific event (vendor's products only)
     * GET /api/vendor-portal/product-event-limits/event/{eventId}
     */
    async getByEvent(eventId) {
        const response = await this.getByEventRaw(eventId);
        return response.data ?? [];
    }
    /**
     * Get by event (raw response)
     */
    getByEventRaw(eventId) {
        return this.get(`event/${eventId}`);
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific product event limit by composite key
     * GET /api/vendor-portal/product-event-limits/{productId}/{eventId}
     */
    async getById(productId, eventId) {
        const response = await this.getByIdRaw(productId, eventId);
        if (!response.data) {
            throw new Error(`Product event limit ${productId}/${eventId} not found`);
        }
        return response.data;
    }
    /**
     * Get by ID (raw response)
     */
    getByIdRaw(productId, eventId) {
        return this.get(`${productId}/${eventId}`);
    }
    // ==================== CREATE ====================
    /**
     * Create a product event limit
     * POST /api/vendor-portal/product-event-limits
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create product event limit');
        }
        return response.data;
    }
    /**
     * Create (raw response)
     */
    createRaw(data) {
        return this.post('', data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a product event limit
     * PUT /api/vendor-portal/product-event-limits/{productId}/{eventId}
     */
    async update(productId, eventId, data) {
        const response = await this.updateRaw(productId, eventId, data);
        if (!response.data) {
            throw new Error(`Failed to update product event limit ${productId}/${eventId}`);
        }
        return response.data;
    }
    /**
     * Update (raw response)
     */
    updateRaw(productId, eventId, data) {
        return this.put(`${productId}/${eventId}`, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a product event limit
     * DELETE /api/vendor-portal/product-event-limits/{productId}/{eventId}
     */
    async remove(productId, eventId) {
        const response = await this.removeRaw(productId, eventId);
        return response.success;
    }
    /**
     * Delete (raw response)
     */
    removeRaw(productId, eventId) {
        return this.delete(`${productId}/${eventId}`);
    }
}
VendorPortalProductEventLimitService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorPortalProductEventLimitService.BASE_PATH = 'api/vendor-portal/product-event-limits';
//# sourceMappingURL=VendorPortalProductEventLimitService.js.map